import type { WallCard, WallGroup } from '@walls/store';
import { isClusterCard } from '../selectors/boardCardSelectors';
import { getCardText } from '../utils/cardDisplay';
import { autoAlignGap } from './autoAlign';
import {
  clusterChildInset,
  defaultClusterWidth,
  getPositionedCardDimensions,
} from './cardRects';

export interface GroupByGroupBoundary {
  height: number;
  width: number;
  x: number;
  y: number;
}

export interface GroupByGroupCardGroup {
  cards: WallCard[];
  group: WallGroup;
}

export interface GroupByGroupClusterPosition {
  groupId: string;
  xPos: number;
  yPos: number;
}

const defaultGap = autoAlignGap;
const ungroupedGroupId = '__ungrouped__';
const ungroupedGroupName = 'Ungrouped';

interface Rect {
  height: number;
  width: number;
  x: number;
  y: number;
}

export function getGroupByGroupCardGroups(
  cards: WallCard[],
  groups: WallGroup[]
): GroupByGroupCardGroup[] {
  const groupClusterNames = new Set(groups.map((group) => normalizeGroupName(group.name)));
  const groupGroups = getSortedGroups(groups)
    .filter((group) => group.participantIds.length > 0)
    .map((group) => ({ group, cards: [] as WallCard[] }));
  const groupGroupsByParticipantId = new Map<string, GroupByGroupCardGroup[]>();

  groupGroups.forEach((group) => {
    group.group.participantIds.forEach((participantId) => {
      const groups = groupGroupsByParticipantId.get(participantId) ?? [];
      groups.push(group);
      groupGroupsByParticipantId.set(participantId, groups);
    });
  });

  const ungroupedCards: WallCard[] = [];

  getRootCards(cards).forEach((card) => {
    if (card.isLocked || !card.ownerId || isExistingGroupCluster(card, groupClusterNames)) {
      return;
    }

    const groups = groupGroupsByParticipantId.get(card.ownerId) ?? [];

    if (groups.length === 0) {
      ungroupedCards.push(card);
      return;
    }

    groups.forEach((group) => group.cards.push(card));
  });

  if (ungroupedCards.length === 0) {
    return groupGroups;
  }

  return [
    ...groupGroups,
    {
      group: {
        id: ungroupedGroupId,
        name: ungroupedGroupName,
        participantIds: [
          ...new Set(ungroupedCards.flatMap((card) => (card.ownerId ? [card.ownerId] : []))),
        ],
      },
      cards: ungroupedCards,
    },
  ];
}

export function getGroupByGroupClusterPositions(
  groups: GroupByGroupCardGroup[],
  boundary: GroupByGroupBoundary,
  options: {
    clusterWidthsByGroupId?: Map<string, number>;
    gap?: number;
    height?: number;
    measuredRects?: Record<string, Rect>;
    width?: number;
  } = {}
): GroupByGroupClusterPosition[] {
  const gap = options.gap ?? defaultGap;
  let xPos = boundary.x;

  return groups.map((group) => {
    const width =
      options.clusterWidthsByGroupId?.get(group.group.id) ??
      options.width ??
      getGroupClusterWidth(group, options.measuredRects);
    const position = {
      groupId: group.group.id,
      xPos: Math.round(xPos),
      yPos: Math.round(boundary.y),
    };

    xPos += width + gap;

    return position;
  });
}

export function getGroupByGroupClusterWidths(
  groups: GroupByGroupCardGroup[],
  existingClustersByGroupId: Map<string, WallCard>,
  measuredRects: Record<string, Rect> = {}
): Map<string, number> {
  return new Map(
    groups.map((group) => {
      const existingCluster = existingClustersByGroupId.get(group.group.id);
      const existingWidth = existingCluster
        ? (measuredRects[existingCluster.id]?.width ?? getPositionedCardDimensions(existingCluster).width)
        : defaultClusterWidth;

      return [
        group.group.id,
        Math.max(existingWidth, getGroupClusterWidth(group, measuredRects)),
      ] as const;
    })
  );
}

export function getExistingGroupClustersByGroupId(
  cards: WallCard[],
  groups: GroupByGroupCardGroup[]
): Map<string, WallCard> {
  const groupsByName = new Map(
    groups.map((group) => [normalizeGroupName(group.group.name), group] as const)
  );
  const clustersByGroupId = new Map<string, WallCard>();

  getRootCards(cards).forEach((card) => {
    if (!isClusterCard(card)) {
      return;
    }

    const group = groupsByName.get(normalizeGroupName(getCardText(card)));

    if (group && !clustersByGroupId.has(group.group.id)) {
      clustersByGroupId.set(group.group.id, card);
    }
  });

  return clustersByGroupId;
}

function getSortedGroups(groups: WallGroup[]) {
  return [...groups].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

function getRootCards(cards: WallCard[]): WallCard[] {
  return cards.filter((card) => !card.parentId);
}

function getGroupClusterWidth(
  group: GroupByGroupCardGroup,
  measuredRects: Record<string, Rect> = {}
) {
  const childWidth = group.cards.reduce(
    (width, card) =>
      Math.max(width, measuredRects[card.id]?.width ?? getPositionedCardDimensions(card).width),
    0
  );

  return Math.max(defaultClusterWidth, childWidth + clusterChildInset * 2);
}

function isExistingGroupCluster(card: WallCard, groupClusterNames: Set<string>): boolean {
  return isClusterCard(card) && groupClusterNames.has(normalizeGroupName(getCardText(card)));
}

function normalizeGroupName(value: string): string {
  return value.trim().toLowerCase();
}
